import { useEffect, useRef } from 'react';

interface UseScrollToFormErrorOptions {
  errorClass?: string;
  scrollBehavior?: ScrollBehavior;
  scrollBlock?: ScrollLogicalPosition;
  scrollDelay?: number;
  focusField?: boolean;
}

export const useScrollToFormError = (
  errors: Record<string, any>,
  isSubmitting: boolean,
  options: UseScrollToFormErrorOptions = {},
) => {
  const {
    errorClass = 'form-error-message',
    scrollBehavior = 'smooth',
    scrollBlock = 'center',
    scrollDelay = 150,
    focusField = true,
  } = options;

  const hasScrolledRef = useRef(false);
  const previousSubmitState = useRef(false);

  useEffect(() => {
    // Когда начинается отправка формы
    if (isSubmitting) {
      hasScrolledRef.current = false;
      previousSubmitState.current = true;
      return;
    }

    // Когда отправка завершена и есть ошибки
    if (
      !isSubmitting &&
      previousSubmitState.current &&
      Object.keys(errors).length > 0 &&
      !hasScrolledRef.current
    ) {
      const timer = setTimeout(() => {
        scrollToFirstError();
      }, scrollDelay);

      return () => clearTimeout(timer);
    }

    previousSubmitState.current = isSubmitting;
  }, [
    errors,
    isSubmitting,
    errorClass,
    scrollBehavior,
    scrollBlock,
    scrollDelay,
    focusField,
  ]);

  const scrollToFirstError = () => {
    if (hasScrolledRef.current) return;

    console.log(' Ищем ошибки с классом:', errorClass);

    // Ищем первый элемент с классом ошибки
    const errorElements = document.getElementsByClassName(errorClass);

    if (errorElements.length > 0) {
      const firstErrorElement = errorElements[0] as HTMLElement;

      console.log(' Найден элемент ошибки:', firstErrorElement.textContent);

      // Плавный скролл к элементу
      firstErrorElement.scrollIntoView({
        behavior: scrollBehavior,
        block: scrollBlock,
        inline: 'nearest',
      });

      // Фокусируем поле если нужно
      if (focusField) {
        focusRelatedField(firstErrorElement);
      }

      hasScrolledRef.current = true;
    } else {
      console.warn(' Не найдены элементы с классом:', errorClass);

      // Fallback: ищем по красному цвету если класс не сработал
      const redElements = document.querySelectorAll(
        '[style*="color: red"], [style*="color:#ff0000"], [style*="color: rgb(255, 0, 0)"]',
      );
      if (redElements.length > 0) {
        const firstRed = redElements[0] as HTMLElement;
        firstRed.scrollIntoView({
          behavior: scrollBehavior,
          block: scrollBlock,
        });
        if (focusField) focusRelatedField(firstRed);
        hasScrolledRef.current = true;
      }
    }
  };

  const focusRelatedField = (errorElement: HTMLElement) => {
    // Ищем связанное поле ввода
    let currentElement: HTMLElement | null = errorElement;

    // Поднимаемся вверх по DOM дереву
    while (currentElement) {
      // Проверяем сам элемент
      if (isFocusable(currentElement)) {
        currentElement.focus();
        return;
      }

      // Ищем внутри элемента
      const focusable = currentElement.querySelector(
        'input, textarea, select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable && isFocusable(focusable as HTMLElement)) {
        (focusable as HTMLElement).focus();
        return;
      }

      currentElement = currentElement.parentElement;
    }
  };

  const isFocusable = (element: HTMLElement): boolean => {
    if (!element.focus) return false;

    const isDisabled = element.hasAttribute('disabled');
    const isHidden = element.hasAttribute('hidden');
    const style = window.getComputedStyle(element);
    const isVisible = style.display !== 'none' && style.visibility !== 'hidden';

    return !isDisabled && !isHidden && isVisible;
  };

  // Функция для ручного вызова
  const manualScrollToError = () => {
    hasScrolledRef.current = false;
    scrollToFirstError();
  };

  return { manualScrollToError };
};
